import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const stats = [
  { value: '99.99%', label: 'Uptime & Reliability' },
  { value: '200+', label: 'Enterprise Cloud Projects' },
  { value: '50+', label: 'Global Certified Architects' },
  { value: '24/7/365', label: 'Proactive Operations Support' },
];

const coreValues = [
  {
    icon: 'fa-solid fa-shield-halved',
    title: 'Zero-Trust Security & Compliance',
    desc: 'Security is not an afterthought. We embed rigorous compliance standards—HIPAA, ISO 26262, ISA/IEC 62443, and NIST—into every architecture we build.',
  },
  {
    icon: 'fa-solid fa-brain',
    title: 'Domain-Driven Intelligence',
    desc: 'We combine deep sector knowledge in Manufacturing, Automotive, Healthcare, Media, and Enterprise Cloud with practical, production-grade AI.',
  },
  {
    icon: 'fa-solid fa-microchip',
    title: 'Cloud-Native & Modern Standards',
    desc: 'From serverless microservices and containerization to automated IaC pipelines, we build systems that scale effortlessly with zero technical debt.',
  },
  {
    icon: 'fa-solid fa-handshake',
    title: 'True Engineering Partnership',
    desc: 'We operate as an extension of your internal engineering teams—driving projects from architectural strategy and PoC through to 24/7 post-launch operations.',
  },
];

const executiveTeam = [
  {
    role: 'Cloud & Infrastructure Practice',
    desc: 'Certified Solutions Architects specializing in multi-cloud migrations (AWS & Azure), FinOps, and automated IaC delivery.',
  },
  {
    role: 'Industrial & AI Engineering Practice',
    desc: 'Industry 4.0 specialists driving IIoT edge integration, computer vision QA models, and predictive maintenance pipelines.',
  },
  {
    role: 'Digital Health & Biotech Practice',
    desc: 'Healthtech veterans focused on FHIR/HL7 interoperability, genomic data pipelines, and FDA/HIPAA compliant SaMD applications.',
  },
  {
    role: 'Automotive & Embedded Systems Practice',
    desc: 'Embedded firmware engineers experts in AUTOSAR, Software-Defined Vehicles (SDVs), and connected telematics clouds.',
  },
];

const milestones = [
  { step: '01', title: 'Consultation & Strategic Discovery', desc: 'We align with your enterprise stakeholders to audit legacy systems, identify friction points, and map ROI goals.' },
  { step: '02', title: 'Architecture & Security Blueprinting', desc: 'Designing resilient landing zones, zero-trust network boundaries, and scalable data pipelines.' },
  { step: '03', title: 'Agile Engineering & AI Integration', desc: 'Building modular microservices, training deep learning models, and provisioning IaC environments.' },
  { step: '04', title: 'Stress Validation & Regulatory Audit', desc: 'Conducting load-testing, penetration testing, and compliance certification checks (HIPAA, ISO, SOC2).' },
  { step: '05', title: 'Deployment & Knowledge Transfer', desc: 'Seamlessly rolling out systems with zero downtime and equipping your internal team with full operational training.' },
  { step: '06', title: 'Managed Operations & FinOps Tuning', desc: 'Providing 24/7/365 infrastructure monitoring, continuous AI model refinement, and active cost optimization.' },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const AboutUs = () => {
  return (
    <div className="about-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              About Lidlbritain Technology Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Engineering Digital Intelligence for <span className="gradient-text-cyan">Global Enterprises</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we bridge the gap between complex industry challenges and modern software paradigms. From enterprise cloud migrations and smart factories to healthcare compliance, connected vehicles, and media streaming—we engineer platforms that scale.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Partner with Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. STATS BANNER */}
      <section className="py-4 border-top border-bottom border-secondary border-opacity-25 bg-dark-overlay">
        <div className="container">
          <div className="row g-4 text-center">
            {stats.map((stat, idx) => (
              <div key={idx} className="col-6 col-md-3">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                >
                  <h2 className="display-5 fw-bold text-cyan mb-1">{stat.value}</h2>
                  <p className="small text-light opacity-75 mb-0">{stat.label}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. OUR MISSION & VISION */}
      <section className="py-5">
        <div className="container">
          <div className="row g-5 align-items-center">
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="fw-bold mb-4">
                Our Mission: Turning Technical Friction into <span className="gradient-text-orange">Competitive Advantage</span>
              </h2>
              <p className="text-light opacity-75 leading-relaxed mb-4">
                Enterprise technology is moving faster than ever, but legacy technical debt, regulatory hurdles, and fragmented systems often slow organizations down.
              </p>
              <p className="text-light opacity-75 leading-relaxed mb-4">
                Lidlbritain was founded to solve this problem. We bring together domain-certified cloud architects, AI engineers, embedded systems specialists, and security practitioners to design and deploy software solutions that are resilient, secure, and future-proof.
              </p>
              <div className="p-3 rounded-3 bg-glass border border-secondary border-opacity-25">
                <p className="small mb-0 text-cyan">
                  <i className="fa-solid fa-quote-left me-2"></i>
                  We don’t just write code—we design enterprise architectures engineered to perform under real-world load, strict compliance, and rapid market evolution.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-3">Key Solutions Domains</h4>
                <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
                  <li className="d-flex align-items-start gap-3">
                    <i className="fa-solid fa-cloud-arrow-up text-orange fs-4 mt-1"></i>
                    <div>
                      <h6 className="fw-bold mb-1">Microsoft & AWS Cloud Solutions</h6>
                      <p className="small opacity-75 mb-0">Serverless architectures, Azure OpenAI, Bedrock Generative AI, and Kubernetes (AKS/EKS).</p>
                    </div>
                  </li>
                  <li className="d-flex align-items-start gap-3">
                    <i className="fa-solid fa-industry text-cyan fs-4 mt-1"></i>
                    <div>
                      <h6 className="fw-bold mb-1">Smart Manufacturing & Industry 4.0</h6>
                      <p className="small opacity-75 mb-0">IIoT edge telemetry, computer vision quality control, and predictive maintenance models.</p>
                    </div>
                  </li>
                  <li className="d-flex align-items-start gap-3">
                    <i className="fa-solid fa-notes-medical text-orange fs-4 mt-1"></i>
                    <div>
                      <h6 className="fw-bold mb-1">Healthcare & Biotech Systems</h6>
                      <p className="small opacity-75 mb-0">FHIR/HL7 interoperability, genomic pipelines, and HIPAA/FDA certified SaMD software.</p>
                    </div>
                  </li>
                  <li className="d-flex align-items-start gap-3">
                    <i className="fa-solid fa-car-tunnel text-cyan fs-4 mt-1"></i>
                    <div>
                      <h6 className="fw-bold mb-1">Automotive & Connected Vehicle Platforms</h6>
                      <p className="small opacity-75 mb-0">Software-Defined Vehicles (SDVs), AUTOSAR firmware, and ADAS dataset training.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. CORE VALUES / PILLARS */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Principles That Drive <span className="gradient-text-cyan">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {coreValues.map((value, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${value.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{value.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed mb-0">{value.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. ENGINEERING SPECIALIZATION & PRACTICE GROUPS */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Our Specialized <span className="gradient-text-orange">Practice Groups</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {executiveTeam.map((team, idx) => (
              <div key={idx} className="col-md-6">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.02 }}
                  className="p-4 rounded-4 bg-glass border border-secondary border-opacity-25 h-100"
                >
                  <h5 className="fw-bold text-cyan mb-2">{team.role}</h5>
                  <p className="text-light opacity-75 small mb-0">{team.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 6. HOW WE WORK / FRAMEWORK */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-cyan">Delivery Engine</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {milestones.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 7. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Ready to Accelerate Your Digital Transformation?</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Connect with Lidlbritain’s senior architects to discuss your enterprise cloud, AI, manufacturing, healthcare, or automotive initiatives.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Schedule an Engineering Consultation &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;